import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import useCascadeDelete from '../hooks/useCascadeDelete';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Departments = () => {
  const { showSuccess, showError } = useNotificationContext();
  const { analyzeDepartmentDeletion, deleteDepartment, loading: deleteLoading } = useCascadeDelete();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, departmentId: null, departmentName: '', analysis: null });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    head: ''
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'department_updated') {
      console.log('🔄 Department update reçu:', data.data?.action);
      fetchDepartments();
    }
  }, []);

  // Use WebSocket context
  const { isConnected } = useWebSocket('departments', handleWebSocketMessage);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/departments?limit=1000&_t=${Date.now()}`);
      setDepartments(Array.isArray(response.data) ? response.data : response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des départements:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (editingDepartment) {
        await axios.put(`${API_URL}/api/departments/${editingDepartment._id}`, formData, config);
        showSuccess('Département modifié avec succès');
      } else {
        await axios.post(`${API_URL}/api/departments`, formData, config);
        showSuccess('Département ajouté avec succès');
      }
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError('Erreur lors de la sauvegarde du département');
    }
  };

  const confirmDelete = async (department) => {
    try {
      const analysis = await analyzeDepartmentDeletion(department._id);
      setDeleteConfirm({ 
        show: true, 
        departmentId: department._id, 
        departmentName: department.name,
        analysis 
      });
    } catch (error) {
      showError('Erreur lors de l\'analyse de suppression');
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.departmentId) {
      await deleteDepartment(deleteConfirm.departmentId);
      fetchDepartments(); // Refresh the list
    }
    setDeleteConfirm({ show: false, departmentId: null, departmentName: '', analysis: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, departmentId: null, departmentName: '', analysis: null });
  };

  const openModal = (department = null) => {
    setEditingDepartment(department);
    setFormData(department ? {
      name: department.name,
      description: department.description || '',
      head: department.head
    } : {
      name: '',
      description: '',
      head: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDepartment(null);
    setFormData({ name: '', description: '', head: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Départements</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <button 
            onClick={() => openModal()} 
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
          >
            + Ajouter
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map(department => (
            <div key={department._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🏢</span>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{department.name}</h3>
                    <p className="text-sm text-gray-600">👤 Chef: {department.head}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={() => openModal(department)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => confirmDelete(department)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              {department.description && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600">{department.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {departments.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun département</h3>
            <p className="text-gray-600">Cliquez sur "Ajouter" pour commencer.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingDepartment ? 'Modifier Département' : 'Ajouter Département'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du département</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chef de département</label>
                  <input
                    type="text"
                    name="head"
                    value={formData.head}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Description optionnelle du département"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingDepartment ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={deleteConfirm.show}
          onClose={cancelDelete}
          onConfirm={handleConfirmDelete}
          title="Supprimer le département"
          message={`Êtes-vous sûr de vouloir supprimer le département ${deleteConfirm.departmentName} ? Cette action supprimera toutes les données associées.`}
          details={deleteConfirm.analysis ? {
            courses: deleteConfirm.analysis.coursesToDelete,
            classes: deleteConfirm.analysis.classesToDelete,
            teachers: deleteConfirm.analysis.teachersToDelete,
            tracks: deleteConfirm.analysis.tracksToDelete,
            sessions: deleteConfirm.analysis.sessionsToDelete
          } : null}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default Departments;